import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { startDiscardingSearchKeyTerm, startLoadingSearchKeyTerms } from "../../app/actions/searchKeyTermActions";
import { FLOW_MODULE_DEFINITIONS, getModuleDisplayLabel } from "../../app/helpers/flowEngineConfigHelper";
import { toFeederFlowRecord } from "../../app/helpers/feederFlowRecordHelper";
import { flowCaseKey } from "../../app/helpers/flowCaseMassHelper";
import { resolveAuthUserId } from "../../app/helpers/flowEditPageHelper";
import { TRAY_VIEW, canOperateCmiRecord } from "../../app/helpers/flowTraySurfaceHelper";
import { loadTrayBoardState, saveTrayBoardState } from "../../app/helpers/flowTrayFilterPersistHelper";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import Pagination from "../generic-ui/Pagination";
import Select from "../generic-ui/Select";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import useFeederTrayRoleSync from "./useFeederTrayRoleSync";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import FlowCaseMassToolbar from "../specialist-flow-engine/FlowCaseMassToolbar";
import { MassiveModuleDownloadButton } from "../specialist-flow-engine/components/MassiveModuleDownloadModal";

const STATUS_META = {
  draft: { key: "specialist.feeder.status.draft", fallback: "Borrador", tone: "secondary" },
  assigned: { key: "specialist.feeder.status.assigned", fallback: "Asignado", tone: "success" },
  finished: { key: "specialist.feeder.status.finished", fallback: "Terminado", tone: "dark" },
  cancelled: { key: "specialist.feeder.status.cancelled", fallback: "Cancelado", tone: "danger" }
};

const TRAY_HISTORY = "history";

const MODULES = Object.entries(FLOW_MODULE_DEFINITIONS).map(([key, def]) => ({
  key,
  id: def.moduleId
}));

const FEEDER_TRAY_SURFACE = "feeder";
const EMPTY_FILTERS = { catalogmoduleId: "", text: "", status: "" };
const PAGE_SIZE_OPTIONS = [2, 3, 4, 5, 10, 15, 20, 25, 50, 100];

const readSavedTray = () => {
  const saved = loadTrayBoardState(FEEDER_TRAY_SURFACE) || {};
  const itemsPerPage = PAGE_SIZE_OPTIONS.includes(Number(saved.itemsPerPage))
    ? Number(saved.itemsPerPage)
    : 10;
  return {
    filters: { ...EMPTY_FILTERS, ...(saved.filters || {}) },
    applied: { ...EMPTY_FILTERS, ...(saved.applied || saved.filters || {}) },
    trayView: saved.trayView || TRAY_VIEW.MINE,
    itemsPerPage
  };
};

const SpecialistFeederTrayScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { rows, loading, titleEnabled } = useSelector((state) => state.searchKeyTerm);
  const auth = useSelector((state) => state.auth);
  const { roleKey, selectedRole } = useActiveSpecialistRole();
  useFeederTrayRoleSync();
  const canCreate = roleKey === "feeder";
  const showMass = roleKey === "feeder";
  const showName = Boolean(titleEnabled);
  const [filters, setFilters] = useState(() => readSavedTray().filters);
  const [applied, setApplied] = useState(() => readSavedTray().applied);
  const [trayView, setTrayView] = useState(() => readSavedTray().trayView);
  const [itemsPerPage, setItemsPerPage] = useState(() => readSavedTray().itemsPerPage);
  const [page, setPage] = useState(0);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const authUserId = resolveAuthUserId(auth?.user);

  useEffect(() => {
    dispatch(startLoadingSearchKeyTerms());
  }, [dispatch]);

  useEffect(() => {
    saveTrayBoardState(FEEDER_TRAY_SURFACE, { filters, applied, trayView, itemsPerPage });
  }, [filters, applied, trayView, itemsPerPage]);

  useEffect(() => {
    setPage(0);
    setSelectedRecords([]);
  }, [applied, trayView, itemsPerPage]);

  const visible = useMemo(() => (rows || []).filter((row) => {
    if (applied.catalogmoduleId && Number(row.catalogmodule_id) !== Number(applied.catalogmoduleId)) {
      return false;
    }
    if (applied.status && row.status !== applied.status) {
      return false;
    }
    const fold = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (applied.text) {
      const haystack = fold([row.title, row.code, row.user_label, row.module_name].join(" "));
      if (!haystack.includes(fold(applied.text))) {
        return false;
      }
    }
    const owner = Number(row.current_user_id || 0);
    const historical = row.status === "assigned" || row.status === "finished";
    if (trayView === TRAY_HISTORY) {
      return historical;
    }
    if (historical || row.status !== "draft") {
      return false;
    }
    if (trayView === TRAY_VIEW.MINE) {
      return owner > 0 && owner === Number(authUserId);
    }
    if (trayView === TRAY_VIEW.POOL) {
      return !owner;
    }
    if (trayView === TRAY_VIEW.GENERAL) {
      return owner > 0;
    }
    return true;
  }).sort((left, right) => (
    Number(left.catalogmodule_id) - Number(right.catalogmodule_id)
    || Number(left.id) - Number(right.id)
  )), [applied, authUserId, rows, trayView]);

  const flowRows = useMemo(
    () => (visible || []).map((row) => toFeederFlowRecord(row, selectedRole?.id)).filter((row) => row?.process),
    [visible, selectedRole]
  );
  const canOperateRecord = (row) => canOperateCmiRecord({
    record: row,
    roleKey,
    roleId: selectedRole?.id,
    userId: authUserId
  });
  const total = flowRows.length;
  const pageRows = flowRows.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const selectableRecords = pageRows.filter((row) => canOperateRecord(row));
  const selectedKeys = new Set(selectedRecords.map(flowCaseKey));
  const allPageSelected = selectableRecords.length > 0
    && selectableRecords.every((row) => selectedKeys.has(flowCaseKey(row)));
  const columnCount = 6 + (showName ? 1 : 0) + (showMass ? 1 : 0);

  const toggleRow = (row) => {
    const key = flowCaseKey(row);
    setSelectedRecords((prev) => (
      prev.some((item) => flowCaseKey(item) === key)
        ? prev.filter((item) => flowCaseKey(item) !== key)
        : [...prev, row]
    ));
  };

  const togglePage = () => {
    if (allPageSelected) {
      const pageKeys = new Set(selectableRecords.map(flowCaseKey));
      setSelectedRecords((prev) => prev.filter((item) => !pageKeys.has(flowCaseKey(item))));
      return;
    }
    setSelectedRecords((prev) => {
      const merged = [...prev];
      const keys = new Set(merged.map(flowCaseKey));
      selectableRecords.forEach((row) => {
        if (!keys.has(flowCaseKey(row))) {
          merged.push(row);
        }
      });
      return merged;
    });
  };

  const refreshTray = () => {
    setSelectedRecords([]);
    dispatch(startLoadingSearchKeyTerms());
  };

  const summaryRows = useMemo(() => (
    applied.catalogmoduleId
      ? (rows || []).filter((row) => Number(row.catalogmodule_id) === Number(applied.catalogmoduleId))
      : (rows || [])
  ), [applied.catalogmoduleId, rows]);
  const countStatus = (status) => summaryRows.filter((row) => row.status === status).length;
  const appliedModule = MODULES.find((item) => String(item.id) === String(applied.catalogmoduleId));

  const cards = [
    { label: t("specialist.feeder.metrics.total", "Total"), value: summaryRows.length },
    { label: t("specialist.feeder.status.assigned", "Asignado"), value: countStatus("assigned") },
    { label: t("specialist.feeder.status.draft", "Borrador"), value: countStatus("draft") },
    { label: t("specialist.feeder.status.cancelled", "Cancelado"), value: countStatus("cancelled") }
  ];

  const moduleLabel = (row) => {
    const match = MODULES.find((item) => item.id === Number(row.catalogmodule_id));
    return match ? getModuleDisplayLabel(match.key, t) : (row.module_name || "—");
  };

  return (
    <FlowEngineGlobalGate>
      <NavbarIconsModuleContext
        moduleProcess="feeder"
        mode="workspace"
        returnTo="/specialist-role-selection"
        returnLabelKey="specialist.selection.role.title"
        createTo="/specialist-feeder/term"
        createLabel={t("specialist.feeder.tray.new", "Nuevo término")}
      />
      <div className="container-fluid py-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h3 className="mb-1">{t("specialist.feeder.tray.title", "Términos clave de búsqueda")}</h3>
            <p className="text-muted mb-0">
              {t(
                "specialist.feeder.tray.subtitle",
                "Bandeja del Alimentador. Filtra por texto, módulo y estado. Cada término guarda un valor por combo."
              )}
            </p>
            <Link to="/specialist-flow-cmi" className="small">
              {t("flow.cmi.open-from-tray", "Abrir panel de control de todos los casos")}
            </Link>
            {!canCreate && (
              <div className="alert alert-info py-2 small mt-3 mb-0">
                {t(
                  "specialist.feeder.tray.readonly",
                  "El rol activo solo puede consultar esta bandeja. Para operar, selecciona el rol Alimentador arriba."
                )}
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {canCreate && (
              <button type="button" className="btn btn-primary" onClick={() => navigate("/specialist-feeder/term")}>
                <i className="fa fa-plus me-2" />
                {t("specialist.feeder.tray.new", "Nuevo término")}
              </button>
            )}
            <button type="button" className="btn btn-outline-secondary" onClick={refreshTray}>
              <i className="fa fa-sync me-2" />
              {t("buttons.refresh", "Actualizar")}
            </button>
          </div>
        </div>

        {appliedModule && (
          <p className="small text-muted mb-2">
            {t(
              "specialist.feeder.metrics.by-module",
              "Sumarizado del módulo {{module}}",
              { module: getModuleDisplayLabel(appliedModule.key, t) }
            )}
          </p>
        )}
        <div className="row g-3 mb-4">
          {cards.map((card) => (
            <div className="col-md-3" key={card.label}>
              <div className="card">
                <div className="card-body">
                  <div className="text-muted small">{card.label}</div>
                  <div className="fs-3 fw-semibold">{loading ? "…" : card.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="feeder-module">
              {t("flow.cmi.col.module", "Módulo")}
            </label>
            <select
              id="feeder-module"
              className="form-select form-select-sm"
              value={filters.catalogmoduleId}
              onChange={(event) => setFilters((prev) => ({ ...prev, catalogmoduleId: event.target.value }))}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {MODULES.map((item) => (
                <option key={item.id} value={item.id}>{getModuleDisplayLabel(item.key, t)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="feeder-text">
              {t("specialist.feeder.tray.text", "Texto")}
            </label>
            <input
              id="feeder-text"
              className="form-control form-control-sm"
              value={filters.text}
              onChange={(event) => setFilters((prev) => ({ ...prev, text: event.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="feeder-status">
              {t("flow.cmi.col.state", "Estado")}
            </label>
            <select
              id="feeder-status"
              className="form-select form-select-sm"
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {Object.entries(STATUS_META).map(([status, meta]) => (
                <option key={status} value={status}>{t(meta.key, meta.fallback)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setApplied(filters)}>
              {t("buttons.search", "Buscar")}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                const empty = { ...EMPTY_FILTERS };
                setFilters(empty);
                setApplied(empty);
              }}
            >
              {t("buttons.clear-filters", "Eliminar filtros")}
            </button>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="btn-group" role="group" aria-label={t("specialist.selection.claim-pool.tabs", "Pestañas de la bandeja")}>
          <button
            type="button"
            className={`btn btn-sm ${trayView === TRAY_VIEW.MINE ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTrayView(TRAY_VIEW.MINE)}
          >
            {t("specialist.selection.claim-pool.inbox", "Mi bandeja")}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${trayView === TRAY_VIEW.POOL ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTrayView(TRAY_VIEW.POOL)}
          >
            {t("specialist.selection.claim-pool.pool", "Bandeja de Autoasignación")}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${trayView === TRAY_VIEW.GENERAL ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTrayView(TRAY_VIEW.GENERAL)}
          >
            {t("specialist.selection.claim-pool.general", "Bandeja General")}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${trayView === TRAY_HISTORY ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTrayView(TRAY_HISTORY)}
          >
            {t("specialist.feeder.tray.history", "Bandeja Asignado/Histórica")}
          </button>
        </div>
        <MassiveModuleDownloadButton surface="feeder" process={appliedModule?.key || ""} />
        </div>
        <p className="small text-muted mb-3">
          {trayView === TRAY_HISTORY
            ? t("specialist.feeder.tray.hint-history", "Términos ya enviados (Asignado) o cerrados (Terminado). El Coordinador cambia el estado al abrir el registro.")
            : trayView === TRAY_VIEW.POOL
              ? t("specialist.feeder.tray.hint-pool", "Borradores sin usuario. Reclámalos para pasarlos a Mi bandeja.")
              : trayView === TRAY_VIEW.GENERAL
                ? t("specialist.feeder.tray.hint-general", "Borradores que ya tienen responsable, incluidos los tuyos.")
                : t("specialist.feeder.tray.hint-mine", "Solo tus borradores. Los asignados están en Bandeja Asignado/Histórica.")}
        </p>

        {showMass && (
          <FlowCaseMassToolbar
            records={pageRows}
            selectedRecords={selectedRecords}
            onSelectionChange={setSelectedRecords}
            onAfterAction={refreshTray}
            roleId={selectedRole?.id}
            roleKey={roleKey}
            poolSurface="feeder"
            subjectKind="search_key_term"
            canSelectRecord={canOperateRecord}
          />
        )}

        <div className="table-responsive mb-4">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                {showMass && (
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={togglePage}
                      disabled={!selectableRecords.length}
                    />
                  </th>
                )}
                <th>{t("flow.cmi.col.module", "Módulo")}</th>
                {showName && <th>{t("flow.cmi.col.name", "Nombre")}</th>}
                <th>{t("general.flow-engine.table.header-code", "Código")}</th>
                <th>{t("flow.cmi.col.state", "Estado")}</th>
                <th>{t("flow.cmi.user", "Usuario")}</th>
                <th>{t("general.flow-engine.table.header-updated", "Actualizado")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="text-center text-muted py-4">
                    {t("specialist.selection.pool.empty", "No hay registros con los filtros seleccionados.")}
                  </td>
                </tr>
              ) : pageRows.map((row) => {
                const meta = STATUS_META[row.status] || STATUS_META.draft;
                return (
                  <tr key={row.id}>
                    {showMass && (
                      <td>
                        {canOperateRecord(row) ? (
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(flowCaseKey(row))}
                            onChange={() => toggleRow(row)}
                          />
                        ) : null}
                      </td>
                    )}
                    <td>{moduleLabel(row)}</td>
                    {showName && <td>{row.title}</td>}
                    <td>{row.code || row.id}</td>
                    <td>
                      <span className={`badge bg-${meta.tone}`}>{t(meta.key, meta.fallback)}</span>
                    </td>
                    <td>{row.user_label || "—"}</td>
                    <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title={t("buttons.view", "Ver")}
                        onClick={() => navigate(`/specialist-feeder/term/${row.id}`)}
                      >
                        <i className="fa fa-eye" />
                      </button>
                      {row.status === "draft" && (
                        Number(row.origin_user_id) === Number(authUserId)
                        || Number(row.current_user_id) === Number(authUserId)
                      ) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-1"
                          title={t("specialist.feeder.tray.discard", "Dar de baja")}
                          onClick={() => {
                            Swal.fire({
                              title: t("specialist.feeder.tray.discard-title", "Dar de baja el borrador"),
                              text: t(
                                "specialist.feeder.tray.discard-text",
                                "El término desaparece de las bandejas. Si ya tiene un DRI relacionado, no se elimina."
                              ),
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonText: t("buttons.delete", "Eliminar"),
                              cancelButtonText: t("buttons.cancel", "Cancelar")
                            }).then(async (result) => {
                              if (!result.isConfirmed) {
                                return;
                              }
                              const ok = await dispatch(startDiscardingSearchKeyTerm(row.id));
                              if (ok) {
                                dispatch(startLoadingSearchKeyTerms());
                              }
                            });
                          }}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
            <div className="small text-muted">
              {t("flow.cmi.results", "{{count}} resultados", { count: total })}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="small">{t("flow.cmi.per-page", "Registros por página")}</span>
              <Select
                listOptions={PAGE_SIZE_OPTIONS}
                pageSize={itemsPerPage}
                onChangeSelect={(event) => setItemsPerPage(Number(event.target.value))}
              />
            </div>
          </div>
          <div className="pagination justify-content-center mt-2">
            <Pagination
              totalPages={Math.ceil(total / (itemsPerPage || 10))}
              totalItems={total}
              currentPage={page + 1}
              itemsPerPage={itemsPerPage}
              onPageChange={(nextPage) => setPage(Math.max(Number(nextPage) - 1, 0))}
            />
          </div>
        </div>
      </div>
    </FlowEngineGlobalGate>
  );
};

export default SpecialistFeederTrayScreen;
